"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.TwilioRemoteVideoTrack = exports.TwilioLocalVideoTrack = exports.TwilioVideoTrack = void 0;
const twilio_video_1 = require("twilio-video");
const rxjs_1 = require("rxjs");
const logger_1 = require("../../util/logger");
const toolbox_1 = require("@streem/toolbox");
class TwilioVideoTrack {
    constructor(track, lightshowEngine) {
        this.track = track;
        this.lightshowEngine = lightshowEngine;
        this.kind = 'videoTrack';
        this.name = this.track.name;
        this.enabled = new rxjs_1.Observable(subscriber => {
            const enabler = () => subscriber.next(true);
            const disabler = () => subscriber.next(false);
            track.on('enabled', enabler);
            track.on('disabled', disabler);
            subscriber.add(() => {
                track.off('enabled', enabler);
                track.off('disabled', disabler);
            });
            subscriber.next(track.isEnabled);
        });
        this.started = new rxjs_1.Observable(subscriber => {
            const starter = () => subscriber.next(true);
            track.on('started', starter);
            subscriber.add(() => {
                track.off('started', starter);
            });
            subscriber.next(track.isStarted);
        });
        this.videoElement = track.attach();
        this.buffer = document.createElement('canvas');
        this.videoElementManager = new toolbox_1.VideoElementManager(this.videoElement);
    }
    scheduleRenderingCallback(callback) {
        const context = this.buffer.getContext('2d');
        (0, toolbox_1.invariant)(context, 'Unable to get 2d rendering context for buffer');
        this.videoElementManager.addHiddenVideoToDOM();
        this.videoElementManager.playVideoWhenLoaded({
            onError: error => logger_1.logger.warn('failed to play video: ', error),
        });
        const stopAnimationLoop = (0, toolbox_1.startAnimationLoop)(() => {
            // https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/readyState
            if (this.videoElement.readyState < this.videoElement.HAVE_CURRENT_DATA) {
                logger_1.logger.debug('Video element is not in a readyState', this.videoElement.readyState);
                return;
            }
            // Sanity check that our video has some resolution greater than 0x0.
            if (this.videoElement.videoHeight <= 0 || this.videoElement.videoWidth <= 0) {
                logger_1.logger.debug('Video element is 0x0px', {
                    width: this.videoElement.videoWidth,
                    height: this.videoElement.videoHeight,
                });
                return;
            }
            if (this.buffer.width !== this.videoElement.videoWidth ||
                this.buffer.height !== this.videoElement.videoHeight) {
                this.buffer.width = this.videoElement.videoWidth;
                this.buffer.height = this.videoElement.videoHeight;
            }
            if (this.lightshowEngine) {
                this.latestCameraFrame = this.lightshowEngine.extractCameraFrame(this.videoElement);
                // Draw the video frame to the buffer canvas, cropping the lightshow pixels.
                const source = this.lightshowEngine.getLightshowAvoidingRect(this.videoElement);
                context.drawImage(this.videoElement, source.x, source.y, source.width, source.height, 0, 0, this.videoElement.videoWidth, this.videoElement.videoHeight);
                callback(this.buffer, this.latestCameraFrame);
            }
            else {
                callback(this.videoElement, this.latestCameraFrame);
            }
        }, this.videoElement);
        return () => {
            stopAnimationLoop();
            this.videoElementManager.removeVideoFromDOM();
        };
    }
}
exports.TwilioVideoTrack = TwilioVideoTrack;
class TwilioLocalVideoTrack extends TwilioVideoTrack {
    constructor(track) {
        var _a;
        super(track);
        this.track = track;
        this.videoElementManager = new toolbox_1.VideoElementManager(this.videoElement);
        this.trackLabel = ((_a = track === null || track === void 0 ? void 0 : track.mediaStreamTrack) === null || _a === void 0 ? void 0 : _a.label) || '';
        const browserSupportsTwilioVideo = twilio_video_1.isSupported;
        if (!browserSupportsTwilioVideo) {
            logger_1.logger.info('Browser is not supported by twilio-video: https://www.twilio.com/docs/video/javascript#supported-browsers');
        }
        this.videoElementManager.webkitVideoFix(() => logger_1.logger.info('Re-initialized video for Safari video fix'));
    }
    setEnabled(enable) {
        if (enable === true) {
            this.track.enable();
        }
        else {
            this.track.disable();
        }
    }
    getMediaTrackConstraints() {
        return this.track.mediaStreamTrack.getConstraints();
    }
    /**
     * Creates a new video track
     * This stops the existing MediaStreamTrack and creates a new MediaStreamTrack.
     * If the LocalVideoTrack is being published to a Room,
     * then all the RemoteParticipants will start receiving media from the newly created MediaStreamTrack.
     * @returns boolean - true if the switch was successful, false if it failed
     */
    restart(constraints) {
        return __awaiter(this, void 0, void 0, function* () {
            // https://media.twiliocdn.com/sdk/js/video/releases/2.13.1/docs/LocalVideoTrack.html#restart__anchor
            try {
                yield this.track.restart(constraints);
                return true;
            }
            catch (error) {
                logger_1.logger.error(`Failed to restart local video track ${this.name}`, error);
                return false;
            }
        });
    }
}
exports.TwilioLocalVideoTrack = TwilioLocalVideoTrack;
class TwilioRemoteVideoTrack extends TwilioVideoTrack {
}
exports.TwilioRemoteVideoTrack = TwilioRemoteVideoTrack;
//# sourceMappingURL=video_tracks.js.map